import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';

const AppShowcase = ({ currentUserId }) => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [relatedApps, setRelatedApps] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAppDetails();
  }, [appId]);

  const fetchAppDetails = async () => {
    try {
      const appDoc = await getDoc(doc(db, 'services', appId));

      if (!appDoc.exists()) {
        // Not a subscription app — might be a credit-based tool. Redirect to /apps/ route
        // which handles mini-SaaS tools, or fallback to marketplace.
        navigate(`/apps/${appId}`, { replace: true });
        return;
      }
      
      const appData = { id: appDoc.id, ...appDoc.data() };
      
      const pricingQuery = query(
        collection(db, 'pricingTiers'),
        where('serviceId', '==', appId),
        where('status', '==', 'active'),
        orderBy('sortOrder')
      );
      
      const pricingSnapshot = await getDocs(pricingQuery);
      const pricing = pricingSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      appData.pricing = pricing;
      setApp(appData);
      setSelectedTier(pricing[0]);
      
      fetchRelatedApps(appData.category);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching app details:', error);
      setLoading(false);
    }
  };

  const fetchRelatedApps = async (category) => {
    try {
      const relatedQuery = query(
        collection(db, 'services'),
        where('category', '==', category),
        where('status', '==', 'active')
      );
      
      const snapshot = await getDocs(relatedQuery);
      const apps = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(a => a.id !== appId)
        .slice(0, 3);
      
      setRelatedApps(apps);
    } catch (error) {
      console.error('Error fetching related apps:', error);
    }
  };

  const handleSubscribe = () => {
    navigate('/payment', { 
      state: { 
        service: app, 
        selectedTier,
        referralApp: appId 
      } 
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading application details...</p>
      </div>
    );
  }

  if (!app) {
    return null;
  }

  return (
    <div className="app-showcase-container">
      <button 
        className="back-button"
        onClick={() => navigate('/marketplace')}
      >
        ← Back to Marketplace
      </button>

      <div className="app-showcase-header">
        <div className="app-info">
          <span className="app-icon-large">{app.icon}</span>
          <div>
            <h1 className="app-title">{app.name}</h1>
            <p className="app-tagline">{app.description}</p>
            <div className="app-meta">
              {app.category && <span className="category-tag">{app.category}</span>}
              {app.new && <span className="badge new">New</span>}
              {app.popular && <span className="badge popular">Popular</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="showcase-content">
        <div className="main-content">
          {app.longDescription && (
            <div className="section">
              <h2>About {app.name}</h2>
              <p>{app.longDescription}</p>
            </div>
          )}

          {app.features && app.features.length > 0 && (
            <div className="section">
              <h2>Key Features</h2>
              <div className="features-grid">
                {app.features.map((feature, idx) => (
                  <div key={idx} className="feature-item">
                    <span className="feature-check">✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {app.screenshots && app.screenshots.length > 0 && (
            <div className="section">
              <h2>Screenshots</h2>
              <div className="screenshots-gallery">
                {app.screenshots.map((screenshot, idx) => (
                  <img 
                    key={idx} 
                    src={screenshot} 
                    alt={`${app.name} screenshot ${idx + 1}`}
                    className="screenshot"
                  />
                ))}
              </div>
            </div>
          )}

          {app.testimonials && app.testimonials.length > 0 && (
            <div className="section">
              <h2>What Our Users Say</h2>
              <div className="testimonials">
                {app.testimonials.map((testimonial, idx) => (
                  <div key={idx} className="testimonial-card">
                    <p>"{testimonial.text}"</p>
                    <cite>- {testimonial.author}</cite>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sidebar">
          <div className="pricing-card sticky">
            <h3>Choose Your Plan</h3>
            <div className="pricing-tiers">
              {app.pricing.map(tier => (
                <div 
                  key={tier.id}
                  className={`tier-option ${selectedTier?.id === tier.id ? 'selected' : ''}`}
                  onClick={() => setSelectedTier(tier)}
                >
                  <div className="tier-header">
                    <h4>{tier.name}</h4>
                    <div className="tier-price">
                      <span className="currency">$</span>
                      <span className="amount">{tier.price}</span>
                      <span className="period">/{tier.billingCycle}</span>
                    </div>
                  </div>
                  {tier.description && (
                    <p className="tier-description">{tier.description}</p>
                  )}
                  {tier.features && (
                    <ul className="tier-features">
                      {tier.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            
            <button 
              className="subscribe-button"
              onClick={handleSubscribe}
              disabled={!selectedTier}
            >
              Subscribe to {selectedTier?.name || 'Plan'}
            </button>
            
            <p className="payment-info">
              <span>🔒</span> Secure payment via Stripe
            </p>
          </div>

          {app.supportInfo && (
            <div className="support-card">
              <h4>Support</h4>
              <p>{app.supportInfo}</p>
            </div>
          )}
        </div>
      </div>

      {relatedApps.length > 0 && (
        <div className="related-apps-section">
          <h2>You Might Also Like</h2>
          <div className="related-apps-grid">
            {relatedApps.map(relatedApp => (
              <div 
                key={relatedApp.id} 
                className="related-app-card"
                onClick={() => navigate(`/app/${relatedApp.id}`)}
              >
                <span className="app-icon">{relatedApp.icon}</span>
                <h4>{relatedApp.name}</h4>
                <p>{relatedApp.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="cross-promotion-banner">
        <div className="banner-content">
          <h3>💡 Discover More AI Solutions</h3>
          <p>Explore our full range of AI-powered applications designed to transform your business operations.</p>
          <button className="banner-cta" onClick={() => navigate('/marketplace')}>
            Browse More Apps
          </button>
        </div>
      </div>
    </div>
  );
};

export default AppShowcase;